import {
  ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
  normalizeRenderConfig,
  sortReportRows,
} from "./renderConfig";
import { buildCsv } from "./agentServiceTimeExport";
import { buildArrivalRatesPerServiceInsights } from "./arrivalRatesPerServiceInsights";

const SUMMARY_HEADERS = Object.freeze([
  "Scope",
  "Branch",
  "Rank",
  "Service",
  "Total Visitors",
  "%",
  "Avg Active Day",
  "Avg Business Day",
  "Min",
  "Max",
  "Active Days",
  "Zero Arrival Business Days",
  "Peak Day",
  "Peak Arrivals",
  "Volatility",
  "Cumulative %",
]);

const DAILY_HEADERS = Object.freeze([
  "Scope",
  "Branch",
  "Service",
  "Period",
  "Visitors",
]);

const toPrimitiveNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const getArrivalRatesPerServiceRows = ({
  data = [],
  branchName = "",
  scope = "",
  branchTotalArrivals,
}) =>
  buildArrivalRatesPerServiceInsights({ branchName, branchTotalArrivals, servicesData: data }).rankedServices
    .filter((service) => toPrimitiveNumber(service?.total))
    .map((service) => ({
      ...service,
      serviceName: service.serviceName || service.name || "",
      branchName,
      scope,
      percentage: service.totalPct,
    }));

export const getArrivalRatesPerServiceRowsForScope = (input, renderConfig) => {
  const config = normalizeRenderConfig(renderConfig, {
    columnDefinitions: ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
  });
  return sortReportRows(getArrivalRatesPerServiceRows(input), config, {
    columnDefinitions: ARRIVAL_RATES_PER_SERVICE_COLUMN_DEFINITIONS,
  });
};

export const getArrivalRatesPerServiceSummaryCsvRows = (scopes = [], renderConfig) =>
  scopes.flatMap((scope) =>
    getArrivalRatesPerServiceRowsForScope(scope, renderConfig).map((row) => [
      row.scope || "",
      row.branchName || "",
      toPrimitiveNumber(row.rank),
      row.serviceName || "",
      toPrimitiveNumber(row.total),
      toPrimitiveNumber(row.totalPct),
      toPrimitiveNumber(row.average),
      toPrimitiveNumber(row.averagePerBusinessDay),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      toPrimitiveNumber(row.activeDays),
      toPrimitiveNumber(row.zeroArrivalBusinessDays),
      row.peakDay || "",
      toPrimitiveNumber(row.peakValue),
      row.volatilityBand || "",
      toPrimitiveNumber(row.cumulativePct),
    ])
  );

export const getArrivalRatesPerServiceDailyCsvRows = (scopes = [], renderConfig) =>
  scopes.flatMap((scope) =>
    getArrivalRatesPerServiceRowsForScope(scope, renderConfig).flatMap((row) =>
      (row.chart || []).map((point) => [
        row.scope || "",
        row.branchName || "",
        row.serviceName || "",
        getPointDate(point),
        toPrimitiveNumber(point.total ?? point.value ?? point.count ?? point.rate),
      ])
    )
  );

const getPointDate = (point) => point.date ?? point.day ?? point.name ?? point.hour ?? "";

const hasHourLabels = (rows) =>
  rows.some((row) => (row.chart || []).some((point) => point.hour !== undefined && point.hour !== null));

const getDayLabels = (rows) =>
  Array.from(
    new Set(
      rows.flatMap((row) =>
        (row.chart || []).map(getPointDate).filter((date) => date !== undefined && date !== null && date !== "")
      )
    )
  );

export const getArrivalRatesPerServiceMergedCsvHeaders = (scopes = [], renderConfig) => {
  const rows = scopes.flatMap((scope) => getArrivalRatesPerServiceRowsForScope(scope, renderConfig));
  const periodLabel = hasHourLabels(rows) ? "Hour" : "Day";
  return [...SUMMARY_HEADERS, ...getDayLabels(rows).map((day) => `${periodLabel} ${day}`)];
};

export const getArrivalRatesPerServiceMergedCsvRows = (scopes = [], renderConfig) => {
  const rows = scopes.flatMap((scope) => getArrivalRatesPerServiceRowsForScope(scope, renderConfig));
  const dayLabels = getDayLabels(rows);
  return rows.map((row) => {
    const valuesByDay = Object.fromEntries(
      (row.chart || []).map((point) => [
        getPointDate(point),
        toPrimitiveNumber(point.total ?? point.value ?? point.count ?? point.rate),
      ])
    );
    return [
      row.scope || "",
      row.branchName || "",
      toPrimitiveNumber(row.rank),
      row.serviceName || "",
      toPrimitiveNumber(row.total),
      toPrimitiveNumber(row.totalPct),
      toPrimitiveNumber(row.average),
      toPrimitiveNumber(row.averagePerBusinessDay),
      toPrimitiveNumber(row.min),
      toPrimitiveNumber(row.max),
      toPrimitiveNumber(row.activeDays),
      toPrimitiveNumber(row.zeroArrivalBusinessDays),
      row.peakDay || "",
      toPrimitiveNumber(row.peakValue),
      row.volatilityBand || "",
      toPrimitiveNumber(row.cumulativePct),
      ...dayLabels.map((day) => valuesByDay[day] ?? ""),
    ];
  });
};

const triggerCsvDownload = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const downloadArrivalRatesPerServiceCsvFiles = ({ scopes, renderConfig, filenamePrefix }) => {
  triggerCsvDownload(
    `${filenamePrefix}.csv`,
    buildCsv(
      getArrivalRatesPerServiceMergedCsvHeaders(scopes, renderConfig),
      getArrivalRatesPerServiceMergedCsvRows(scopes, renderConfig)
    )
  );
};
